// Public forgot-password page. Submits an email to /api/auth/forgot and ALWAYS shows the same
// generic confirmation — it never reveals whether the email is registered.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { postAuth } from '../lib/authPublic';

const GENERIC = "If that email is registered, we've sent a reset link. Check your inbox (and spam) — the link expires in 30 minutes.";

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    // The endpoint always returns a generic 200; we show the same confirmation regardless of
    // the response (including a network error we can't distinguish from "no such user").
    await postAuth('/api/auth/forgot', { email });
    setLoading(false);
    setSubmitted(true);
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.header}>
          <h1 style={S.title}>Reset your password</h1>
          <p style={S.subtitle}>Enter your account email and we'll send a reset link.</p>
        </div>

        {submitted ? (
          <>
            <div style={S.infoBox} role="status">{GENERIC}</div>
            <Link to="/login" style={S.link}>← Back to sign in</Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={S.form} noValidate>
            <div style={S.field}>
              <label style={S.label} htmlFor="email">Email address</label>
              <input
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                style={S.input} placeholder="you@flightstandards.com.au" disabled={loading}
              />
            </div>
            <button type="submit" style={S.button} disabled={loading || !email}>
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
            <Link to="/login" style={S.link}>← Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  card: { width: '100%', maxWidth: 400, background: '#1e293b', borderRadius: 12, padding: '40px 36px', boxShadow: '0 4px 24px rgba(0,0,0,0.4)' },
  header: { textAlign: 'center', marginBottom: 28 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#f1f5f9', letterSpacing: -0.5 },
  subtitle: { margin: '8px 0 0', fontSize: 13, color: '#64748b' },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: '#94a3b8' },
  input: { padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', fontSize: 15, outline: 'none' },
  button: { padding: 12, background: '#3b82f6', border: 'none', borderRadius: 6, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  infoBox: { padding: '12px 14px', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 6, color: '#bae6fd', fontSize: 14, lineHeight: 1.5, marginBottom: 20 },
  link: { display: 'block', textAlign: 'center', fontSize: 13, color: '#7dd3fc', textDecoration: 'none', marginTop: 4 },
};
